// The win-back emails' unsubscribe page (docs/22 §3; copy in docs/24).
//
// Every win-back email links here with ?t=<token>: the account id, signed by the API, so it
// works from the inbox without logging in. The page makes the call itself (a POST), so a mail
// scanner that follows the link doesn't unsubscribe anyone. One click stops every win-back
// flow; job alerts and account emails go on, and the page says so.

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import SubmissionLayout from "../common/SubmissionLayout";
import { getSubmissionFlowApi } from "../../../shared/api/submission";
import { trackSubmissionEvent } from "../analytics";
import { membershipPrimary } from "../common/membershipButton";

interface Props {
  history: any;
  location: any;
}

type State = "working" | "done" | "broken" | "unavailable";

const SUPPORT = "support@castingworkbook.com";

const SubmissionUnsubscribePage: React.FC<Props> = ({ history, location }) => {
  const [state, setState] = useState<State>("working");

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("t") || "";
    if (!token) {
      setState("broken");
      return;
    }
    getSubmissionFlowApi()
      .unsubscribeWinBack(token)
      .then((resp) => {
        trackSubmissionEvent("sf_winback_unsubscribed", { already: !!resp.alreadyUnsubscribed });
        setState("done");
      })
      .catch((err: any) => {
        const code = err && err.errorCode;
        trackSubmissionEvent("sf_winback_unsubscribe_failed", { code: code || "network" });
        // A link that can't be read is broken for good; anything else may work later.
        setState(code === "invalidLink" ? "broken" : "unavailable");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCastingCalls = () => history.push("/submission/casting-calls");

  return (
    <SubmissionLayout>
      <Centered aria-busy={state === "working"} aria-live="polite">
        {state === "working" && <Text>{i18next.t("One moment…")}</Text>}

        {state === "done" && (
          <>
            <Title>{i18next.t("You won't get these reminders anymore")}</Title>
            <Text>
              {i18next.t(
                "We won't send you any more reminders about unfinished submissions. Job alerts and account emails, like codes and password resets, still arrive."
              )}
            </Text>
            <PrimaryButton type="button" onClick={onCastingCalls}>
              {i18next.t("See casting calls")}
            </PrimaryButton>
          </>
        )}

        {state === "broken" && (
          <>
            <Title>{i18next.t("This link doesn't work")}</Title>
            <Text>
              {i18next.t(
                "It may be incomplete: copy the whole link from the email, or write to {{email}}.",
                { email: SUPPORT }
              )}
            </Text>
            <Support>{SUPPORT}</Support>
          </>
        )}

        {state === "unavailable" && (
          <>
            <Title>{i18next.t("We couldn't stop the reminders just now")}</Title>
            <Text>{i18next.t("Please open the link again in a few minutes.")}</Text>
          </>
        )}
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 48px 0 16px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: #122640;
  margin: 0;
  text-wrap: balance;
`;

const Text = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #53627c;
  margin: 0;
  max-width: 34em;
`;

// Selectable, because a mailto link may do nothing where the page is opened.
const Support = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #122640;
  margin: 0;
  user-select: all;
`;

const PrimaryButton = styled.button`
  width: 100%;
  max-width: 360px;
  margin-top: 8px;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;

  &:focus-visible {
    outline: 3px solid #0f9bf0;
    outline-offset: 2px;
  }
`;

export default SubmissionUnsubscribePage;
